/**
 * ShareDialog shows the room's shareable URL with a copy button.
 *
 * Why: video calls make "who's actually in this room" matter more; a fast,
 * obvious way to invite others (beyond the room menu) reduces friction.
 * Uses FocusNavigationDialog so Copy/Close are reachable via gamepad D-pad.
 *
 * When to use: Rendered by Header, triggered by ShareIconButton.
 */
import * as React from "react"
import * as Dialog from "@radix-ui/react-dialog"
import { FocusNavigationDialog, useDialogNavigation, DialogNavigationItem } from "../../library/FocusNavigationDialog/FocusNavigationDialog"
import { ActionButton } from "../../library/ActionButton/ActionButton"
import { CopyIcon } from "../../library/Icons/CopyIcon"
import { CheckIcon } from "../../library/Icons/CheckIcon"
import { headerTestIds } from "./header.testIds"

export interface ShareDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  roomUrl: string
  /** Resolves false (rather than throwing) if the clipboard write failed. */
  onCopyLink: () => Promise<boolean>
  trigger: React.ReactElement
}

interface ShareDialogItem extends DialogNavigationItem {
  type: 'copy' | 'close'
}

function ShareDialogContent({
  roomUrl,
  copied,
  onCopy,
}: {
  roomUrl: string
  copied: boolean
  onCopy: () => void
}) {
  const navigation = useDialogNavigation()
  const copyButtonRef = React.useRef<HTMLButtonElement>(null)
  const closeButtonRef = React.useRef<HTMLButtonElement>(null)

  React.useEffect(() => {
    navigation.registerItem(0, copyButtonRef.current)
    navigation.registerItem(1, closeButtonRef.current)
  }, [navigation])

  return (
    <>
      <p className="text-sm text-muted mb-3">Share this link so others can join the room.</p>
      <input
        type="text"
        readOnly
        value={roomUrl}
        onFocus={(e) => e.currentTarget.select()}
        data-testid={headerTestIds.shareDialogUrlInput}
        className="w-full rounded-md border border-basic bg-secondary-bg px-3 py-2 text-sm text-primary mb-4"
      />
      <div className="flex gap-2 justify-end">
        <Dialog.Close asChild>
          <ActionButton ref={closeButtonRef} variant="outline" isFocused={navigation.focusedIndex === 1}>
            Close
          </ActionButton>
        </Dialog.Close>
        <ActionButton
          ref={copyButtonRef}
          variant="default"
          onClick={onCopy}
          testId={headerTestIds.shareDialogCopyButton}
          isFocused={navigation.focusedIndex === 0}
          accentBackground
          glowOnFocus
          className="gap-2"
        >
          {copied ? <CheckIcon width={16} height={16} /> : <CopyIcon width={16} height={16} />}
          {copied ? 'Copied!' : 'Copy Link'}
        </ActionButton>
      </div>
    </>
  )
}

export const ShareDialog = React.forwardRef<HTMLButtonElement, ShareDialogProps>(
  ({ open, onOpenChange, roomUrl, onCopyLink, trigger }, ref) => {
    const [copied, setCopied] = React.useState(false)

    const handleCopy = React.useCallback(async () => {
      const succeeded = await onCopyLink()
      if (!succeeded) return
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }, [onCopyLink])

    // Reset the "Copied!" feedback each time the dialog is reopened.
    React.useEffect(() => {
      if (open) setCopied(false)
    }, [open])

    const navigationItems: ShareDialogItem[] = React.useMemo(() => [
      { id: 'copy', type: 'copy' as const },
      { id: 'close', type: 'close' as const },
    ], [])

    const handleNavigationSelect = React.useCallback((item: DialogNavigationItem) => {
      const dialogItem = item as ShareDialogItem
      if (dialogItem.type === 'copy') {
        handleCopy()
      } else {
        onOpenChange(false)
      }
    }, [handleCopy, onOpenChange])

    return (
      <FocusNavigationDialog
        open={open}
        onOpenChange={onOpenChange}
        trigger={trigger}
        title="Share Room"
        testId={headerTestIds.shareDialog}
        navigationItems={navigationItems}
        onNavigationSelect={handleNavigationSelect}
        ref={ref}
      >
        <ShareDialogContent roomUrl={roomUrl} copied={copied} onCopy={handleCopy} />
      </FocusNavigationDialog>
    )
  }
)
ShareDialog.displayName = "ShareDialog"
